import { ReactNode, useState } from "react";
import { BoundaryRow } from "../../models/BoundaryRow";
import { ExtractedTableData } from "../../models/extraction/ExtractedTableData";
import { HighlightColours } from "../../models/HighlightColors";
import { Matcher } from "../../models/matchers/Matcher";
import { PageSpans } from "../../models/Page";
import { isBodyRow } from "../../types/RowType";
import { MatcherEditor } from "../matcherEditing/MatcherEditor";
import { HighlightedSpanRows } from "../SpansHighlightable";
import { ExpandingTooltip } from "../tooltips/ExpandingTooltip";

export { TableBoundaryEditor };

type Props = {
  showRows?: boolean;
  /**
   * Override label.
   */
  label: string;
  labelTooltip?: ExpandingTooltip;
  pageIds: string[];
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
  extractedData?: ExtractedTableData;
  /**
   * CSS class to add to root element to dynamically color the MatcherEditor label.
   */
  className: "start" | "soft" | "hard";
  matcher: Matcher;
  update(matcher: Matcher): void;
  /* Default Matcher Threshold, used when changing Matcher type. */
  defaultMatcherThreshold: number;
  button?: ReactNode;
};

const TableBoundaryEditor = (props: Props) => {
  const [showRows, setShowRows] = useState<boolean>(props.showRows ?? false);

  let labelColor = "";
  switch (props.className) {
    case "start":
      labelColor = HighlightColours.getHighlightColor(0);
      break;
    case "soft":
      labelColor = HighlightColours.getHighlightColor(1);
      break;
    case "hard":
      labelColor = HighlightColours.getHighlightColor(2);
      break;
    default:
      "white";
  }

  return (
    // TODO: rename to tableBoundaryEditor
    <div className={"regexTableBoundaryEditor " + props.className}>
      <MatcherEditor
        {...props}
        onClickLabel={() => setShowRows(!showRows)}
        key={props.matcher.getMatcherId() + props.label}
        index={0}
        labelColor={"black"}
        labelBgColor={labelColor}
        matcher={props.matcher}
        changedMatcherType={props.update}
        update={props.update}
        button={props.button}
      />
      {/* Show Span highlights */}
      {showRows && props.extractedData && (
        <HighlightedSpanRows
          getPageSpansById={props.getPageSpansById}
          pageIds={props.pageIds}
          highlightedRows={
            props.extractedData?.extraction?.rows
              .filter((r) => !isBodyRow(r))
              .map((x) => ({
                pageId: x.pageId ?? "",
                rowNumber: x.pageRowNumber ?? "",
                colorIndex: BoundaryRow.getHighlightColorIndexForRowType(
                  x.rowType,
                ),
              })) ?? null
          }
        />
      )}
    </div>
  );
};
